/**
 * Peer Evaluations React Query Hooks
 *
 * Custom hooks for 360-degree feedback peer evaluations.
 * Provides data fetching and mutations for both authenticated and public endpoints.
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  peerEvaluationApi,
  publicPeerEvaluationApi,
  type CreatePeerEvaluationInput,
  type SubmitPeerEvaluationInput,
  type UpdatePeerEvaluationInput,
} from '../../lib/peerEvaluationApi';

// ============================================
// QUERY KEYS
// ============================================

/**
 * Query key factory for peer evaluations
 *
 * Follows React Query best practices for hierarchical key structure.
 * Enables granular cache invalidation.
 */
export const peerEvaluationKeys = {
  /** Base key for all peer evaluation queries */
  all: ['peer-evaluations'] as const,

  /** Key for list queries */
  lists: () => [...peerEvaluationKeys.all, 'list'] as const,

  /** Key for specific employee's peer evaluations list */
  list: (employeeId: number) => [...peerEvaluationKeys.lists(), employeeId] as const,

  /** Key for detail queries */
  details: () => [...peerEvaluationKeys.all, 'detail'] as const,

  /** Key for specific peer evaluation detail */
  detail: (id: number) => [...peerEvaluationKeys.details(), id] as const,

  /** Key for public peer evaluation (separate namespace, not invalidated by auth actions) */
  public: (token: string) => ['public-peer-evaluation', token] as const,
};

// ============================================
// AUTHENTICATED HOOKS
// ============================================

/**
 * Hook to fetch peer evaluations for an employee
 *
 * @param employeeId - Employee ID to fetch peer evaluations for
 * @returns Query result with peer evaluations list
 *
 * @example
 * ```tsx
 * const { data, isLoading } = usePeerEvaluations(employeeId);
 * ```
 */
export function usePeerEvaluations(employeeId: number) {
  return useQuery({
    queryKey: peerEvaluationKeys.list(employeeId),
    queryFn: () => peerEvaluationApi.getByEmployeeId(employeeId),
    enabled: employeeId > 0,
  });
}

/**
 * Hook to fetch a single peer evaluation by ID
 *
 * @param id - Peer evaluation ID
 * @returns Query result with peer evaluation detail
 *
 * @example
 * ```tsx
 * const { data, isLoading } = usePeerEvaluation(peerEvalId);
 * ```
 */
export function usePeerEvaluation(id: number) {
  return useQuery({
    queryKey: peerEvaluationKeys.detail(id),
    queryFn: () => peerEvaluationApi.getById(id),
    enabled: id > 0,
  });
}

/**
 * Hook to create a peer evaluation
 *
 * Automatically invalidates the list cache for the employee after success.
 *
 * @returns Mutation hook for creating peer evaluations
 *
 * @example
 * ```tsx
 * const createMutation = useCreatePeerEvaluation();
 *
 * createMutation.mutate({
 *   employeeId: 1,
 *   evaluatorId: 2,
 *   taskIds: [1, 2, 3],
 *   competencyIds: [1, 2],
 * });
 * ```
 */
export function useCreatePeerEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (input: CreatePeerEvaluationInput) => peerEvaluationApi.create(input),
    onSuccess: () => {
      // Invalidate all peer evaluation lists (covers both specific employee and dashboard views)
      queryClient.invalidateQueries({
        queryKey: peerEvaluationKeys.lists(),
      });
    },
  });
}

/**
 * Hook to update a peer evaluation (concept only)
 *
 * Automatically invalidates caches after success.
 *
 * @returns Mutation hook for updating peer evaluations
 *
 * @example
 * ```tsx
 * const updateMutation = useUpdatePeerEvaluation();
 *
 * updateMutation.mutate({
 *   id: peerEvalId,
 *   data: {
 *     tasks: [{ taskId: 1, score: 4, remarks: 'Good work' }],
 *     competencies: [{ competencyId: 1, score: 5 }],
 *     generalRemarks: 'Overall great performance',
 *   },
 * });
 * ```
 */
export function useUpdatePeerEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: UpdatePeerEvaluationInput }) =>
      peerEvaluationApi.update(id, data),
    onSuccess: (updatedEval) => {
      // Update the detail cache with new data
      queryClient.setQueryData(peerEvaluationKeys.detail(updatedEval.id), updatedEval);
      // Invalidate lists to reflect any changes
      queryClient.invalidateQueries({
        queryKey: peerEvaluationKeys.lists(),
      });
    },
  });
}

/**
 * Hook to delete a peer evaluation (concept only)
 *
 * Automatically invalidates caches after success.
 *
 * @returns Mutation hook for deleting peer evaluations
 *
 * @example
 * ```tsx
 * const deleteMutation = useDeletePeerEvaluation();
 *
 * deleteMutation.mutate({
 *   id: peerEvalId,
 *   employeeId: employeeId, // For cache invalidation
 * });
 * ```
 */
export function useDeletePeerEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id }: { id: number; employeeId: number }) =>
      peerEvaluationApi.delete(id),
    onSuccess: (_, { id }) => {
      // Remove detail from cache
      queryClient.removeQueries({
        queryKey: peerEvaluationKeys.detail(id),
      });
      // Invalidate all peer evaluation lists (covers both specific employee and dashboard views)
      queryClient.invalidateQueries({
        queryKey: peerEvaluationKeys.lists(),
      });
    },
  });
}

// ============================================
// PUBLIC HOOKS (for /evaluatie/:token page)
// ============================================

/**
 * Hook to fetch public peer evaluation form by token
 *
 * Used in the /evaluatie/:token page to display the form.
 *
 * @param token - Secure token from the peer evaluation link
 * @returns Query result with form data (employee name, tasks, competencies, score criteria)
 *
 * @example
 * ```tsx
 * const { data, isLoading, error } = usePublicPeerEvaluation(token);
 *
 * if (error) {
 *   // Token invalid or evaluation already submitted
 * }
 * ```
 */
export function usePublicPeerEvaluation(token: string) {
  return useQuery({
    queryKey: peerEvaluationKeys.public(token),
    queryFn: () => publicPeerEvaluationApi.getByToken(token),
    enabled: !!token && token.length > 0,
    // Don't retry on 404 (invalid/used token) or 400 (validation)
    retry: (failureCount, error) => {
      if (error instanceof Error && error.message.includes('niet gevonden')) {
        return false;
      }
      return failureCount < 2;
    },
    // Cache for 5 minutes (form data doesn't change often)
    staleTime: 5 * 60 * 1000,
  });
}

/**
 * Hook to submit public peer evaluation
 *
 * @returns Mutation hook for submitting peer evaluation
 *
 * @example
 * ```tsx
 * const submitMutation = useSubmitPublicPeerEvaluation();
 *
 * submitMutation.mutate({
 *   token: 'abc123...',
 *   data: {
 *     taskRemarks: [{ taskId: 1, remarks: 'Good work' }],
 *     competencyRemarks: [{ competencyId: 1, remarks: 'Excellent' }],
 *     generalRemarks: 'Overall great performance',
 *   },
 * });
 * ```
 */
export function useSubmitPublicPeerEvaluation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ token, data }: { token: string; data: SubmitPeerEvaluationInput }) =>
      publicPeerEvaluationApi.submit(token, data),
    onSuccess: (_, { token }) => {
      // Remove the form data from cache (evaluation is now submitted)
      queryClient.removeQueries({
        queryKey: peerEvaluationKeys.public(token),
      });
    },
  });
}
